import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  FaCheckCircle,
  FaChevronRight,
  FaExclamationCircle,
} from "react-icons/fa";
import { useMutation } from "@tanstack/react-query";
import { Navbar, Header, Footer, Button } from "../components";
import customAxios from "../utils/customAxios";

import { styles } from "../styles";

const KontakPage = () => {
  const { t } = useTranslation();

  // 1. Setup state to hold form data matching the backend schema
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // 2. Handle input changes dynamically
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // 3. Setup TanStack mutation for the POST request
  const mutation = useMutation({
    mutationFn: async (newContactMessage) => {
      // Adjust the endpoint string based on your actual API route
      const response = await customAxios.post(
        "contact-messages",
        newContactMessage,
      );
      return response.data;
    },
    onSuccess: () => {
      // Reset the form upon successful submission
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    },
  });

  // 4. Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  // Check if all fields have at least some text (ignoring blank spaces)
  const isFormValid =
    formData.name.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.phone.trim() !== "" &&
    formData.message.trim() !== "";

  // The button should be disabled if the form is incomplete OR if it's currently submitting
  const isButtonDisabled = !isFormValid || mutation.isPending;

  return (
    <>
      <Navbar />
      <Header title={t("contact.header")} />
      <section className={`${styles.paddingX} py-20`}>
        <div className="grid w-full place-items-center">
          <form
            onSubmit={handleSubmit}
            className="max-w-[1152px] w-full flex flex-col"
          >
            {/* Success State Banner */}
            {mutation.isSuccess && (
              <div className="flex items-center gap-3 p-4 mb-6 text-green-800 bg-green-100 border border-green-300 rounded-md">
                <FaCheckCircle className="text-xl" />
                <p className={`${styles.textLMediumM}`}>
                  Pesan Anda berhasil dikirim. Kami akan segera menghubungi
                  Anda.
                </p>
              </div>
            )}

            {/* Error State Banner */}
            {mutation.isError && (
              <div className="flex items-center gap-3 p-4 mb-6 text-red-800 bg-red-100 border border-red-300 rounded-md">
                <FaExclamationCircle className="text-xl" />
                <p className={`${styles.textLMediumM}`}>
                  Gagal mengirim pesan. Silakan coba lagi nanti.
                </p>
              </div>
            )}

            <label
              htmlFor="name"
              className={`mb-2 text-primary-40 ${styles.headingS}`}
            >
              <Trans i18nKey="contact.name.label" />
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="px-4 py-3 mb-6 border focus:outline-0 border-neutral-70 placeholder:text-neutral-80"
              placeholder={t("contact.name.placeholder")}
            />

            <label
              htmlFor="email"
              className={`mb-2 text-primary-40 ${styles.headingS}`}
            >
              <Trans i18nKey="contact.email.label" />
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="px-4 py-3 mb-6 border focus:outline-0 border-neutral-70 placeholder:text-neutral-80"
              placeholder={t("contact.email.placeholder")}
            />

            <label
              htmlFor="phone"
              className={`mb-2 text-primary-40 ${styles.headingS}`}
            >
              <Trans i18nKey="contact.tel.label" />
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="px-4 py-3 mb-6 border focus:outline-0 border-neutral-70 placeholder:text-neutral-80"
              placeholder={t("contact.tel.placeholder")}
            />

            <label
              htmlFor="message"
              className={`mb-2 text-primary-40 ${styles.headingS}`}
            >
              <Trans i18nKey="contact.content.label" />
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={handleChange}
              required
              className="px-4 py-3 mb-6 h-[240px] border focus:outline-0 border-neutral-70 placeholder:text-neutral-80 resize-none"
              placeholder={t("contact.content.placeholder")}
            />

            <div className="flex items-center justify-center w-full">
              <Button
                title={mutation.isPending ? "Mengirim..." : t("contact.submit")}
                icon={!mutation.isPending && <FaChevronRight />}
                customization={`text-neutral-10 ${
                  isButtonDisabled
                    ? "bg-neutral-50 cursor-not-allowed"
                    : "bg-primary-40"
                }`}
                type="submit"
                // The disabled attribute prevents multiple clicks while fetching
                disabled={isButtonDisabled}
              />
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default KontakPage;
